import { hash } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismInput, PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import { t } from 'ember-intl';
import type { SubscriptionOption } from 'lumen/components/settings/users/subscription-select';
import SubscriptionSelect from 'lumen/components/settings/users/subscription-select';
import { PRODUCTS } from 'lumen/utils/constants/users-constants';

interface FilterOption extends Option {
  id: string | null;
}

interface InlineFiltersSignature {
  Args: {
    isAllSubscriptions: boolean;
    isLoading?: boolean;
    onProductChange: (product: string | null) => void;
    onRoleChange: (role: string | null) => void;
    onSearchChange: (event: Event) => void;
    onSubscriptionChange: (subscription: SubscriptionOption | null) => void;
    product: string | null;
    role: string | null;
    search: string;
    subscription: SubscriptionOption | null;
    userCount: number;
  };
}

export default class InlineFilters extends Component<InlineFiltersSignature> {
  @service declare intl: Services['intl'];

  @cached
  get productOptions(): FilterOption[] {
    return [
      {
        id: null,
        label: this.intl.t('settings.users.filter.all_products'),
      },
      {
        id: PRODUCTS.BI_BENCHMARKING,
        label: this.intl.t('settings.users.column.bi_benchmarking'),
      },
      {
        id: PRODUCTS.RATE_INSIGHT,
        label: this.intl.t('settings.users.column.rate_insight_market_insight'),
      },
    ];
  }

  @cached
  get roleOptions(): FilterOption[] {
    return [
      {
        id: null,
        label: this.intl.t('settings.users.filter.all_roles'),
      },
      { id: 'admin', label: this.intl.t('useraccess_rights_admin') },
      { id: 'write', label: this.intl.t('useraccess_rights_write') },
      { id: 'read', label: this.intl.t('useraccess_rights_read') },
    ];
  }

  get selectedProduct(): FilterOption {
    return (
      this.productOptions.find((option) => option.id === this.args.product) ??
      this.productOptions[0]!
    );
  }

  get selectedRole(): FilterOption {
    return (
      this.roleOptions.find((option) => option.id === this.args.role) ??
      this.roleOptions[0]!
    );
  }

  @action
  updateProduct(option: FilterOption): void {
    this.args.onProductChange(option.id);
  }

  @action
  updateRole(option: FilterOption): void {
    this.args.onRoleChange(option.id);
  }

  <template>
    {{#if @isAllSubscriptions}}
      {{#if @isLoading}}
        <div class="loading-pulse mr-auto w-[80px]"></div>
      {{else}}
        <span
          class="text-300--regular mr-auto"
          data-test-id="managed-user-count"
        >
          {{t "settings.users.count" count=@userCount}}
        </span>
      {{/if}}
    {{/if}}
    <div>
      <PrismInput
        @iconAfter={{hash name="search"}}
        @onInput={{@onSearchChange}}
        @placeholder={{t "settings.users.filter.search_placeholder"}}
        @removePadding={{true}}
        @value={{@search}}
        data-test-id="settings-users-search-input"
      />
    </div>
    <SubscriptionSelect
      @onChange={{@onSubscriptionChange}}
      @value={{@subscription}}
    />
    {{#unless @isAllSubscriptions}}
      <PrismSelectInput
        @onChange={{this.updateRole}}
        @options={{this.roleOptions}}
        @placeholder={{t "settings.users.filter.all_roles"}}
        @value={{this.selectedRole}}
        data-test-id="role-filter"
      />
      <PrismSelectInput
        @onChange={{this.updateProduct}}
        @options={{this.productOptions}}
        @placeholder={{t "settings.users.filter.all_products"}}
        @value={{this.selectedProduct}}
        data-test-id="product-filter"
      />
    {{/unless}}
  </template>
}
